import type { BenefitSummary } from "@yutai/shared"
import { minAcquisitionAmount } from "./filterBenefits.js"

const DEFAULT_WEIGHTS = {
  categories: 0.35,
  tags: 0.25,
  acquisitionAmount: 0.15,
  recordMonths: 0.1,
  benefitYield: 0.1,
  industry: 0.05,
} as const

const MIN_SCORE_THRESHOLD = 0.15
const DEFAULT_LIMIT = 6

export interface SimilarBenefit {
  benefit: BenefitSummary
  score: number
}

function jaccard<T>(left: readonly T[], right: readonly T[]): number | null {
  if (left.length === 0 && right.length === 0) return null
  const leftSet = new Set(left)
  const rightSet = new Set(right)
  let intersection = 0
  for (const value of leftSet) {
    if (rightSet.has(value)) intersection++
  }
  const union = leftSet.size + rightSet.size - intersection
  if (union === 0) return null
  return intersection / union
}

function proximity(left: number, right: number): number {
  const max = Math.max(Math.abs(left), Math.abs(right), 1)
  return 1 - Math.min(Math.abs(left - right) / max, 1)
}

function weightedScore(dimensions: { weight: number; score: number | null }[]): number {
  let totalWeight = 0
  let weightedSum = 0
  for (const { weight, score } of dimensions) {
    if (score === null) continue
    totalWeight += weight
    weightedSum += weight * score
  }
  if (totalWeight === 0) return 0
  return weightedSum / totalWeight
}

export function similarityScore(target: BenefitSummary, candidate: BenefitSummary): number {
  let categoryWeight: number = DEFAULT_WEIGHTS.categories
  let tagWeight: number = DEFAULT_WEIGHTS.tags
  if (target.categories.length === 0) {
    tagWeight += categoryWeight
    categoryWeight = 0
  }

  const targetAcquisition = minAcquisitionAmount(target)
  const candidateAcquisition = minAcquisitionAmount(candidate)

  return weightedScore([
    { weight: categoryWeight, score: jaccard(target.categories, candidate.categories) },
    { weight: tagWeight, score: jaccard(target.tags, candidate.tags) },
    {
      weight: DEFAULT_WEIGHTS.acquisitionAmount,
      score:
        targetAcquisition != null && candidateAcquisition != null
          ? proximity(targetAcquisition, candidateAcquisition)
          : null,
    },
    {
      weight: DEFAULT_WEIGHTS.recordMonths,
      score: jaccard(target.recordMonths, candidate.recordMonths),
    },
    {
      weight: DEFAULT_WEIGHTS.benefitYield,
      score:
        target.benefitYield != null && candidate.benefitYield != null
          ? proximity(target.benefitYield, candidate.benefitYield)
          : null,
    },
    {
      weight: DEFAULT_WEIGHTS.industry,
      score: target.industry === candidate.industry ? 1 : 0,
    },
  ])
}

function compareSimilarBenefits(left: SimilarBenefit, right: SimilarBenefit): number {
  if (right.score !== left.score) return right.score - left.score
  const leftYield = left.benefit.benefitYield ?? -1
  const rightYield = right.benefit.benefitYield ?? -1
  if (rightYield !== leftYield) return rightYield - leftYield
  return left.benefit.id.localeCompare(right.benefit.id, "ja")
}

function rankCandidates(target: BenefitSummary, allBenefits: BenefitSummary[]): SimilarBenefit[] {
  return allBenefits
    .filter((benefit) => benefit.id !== target.id)
    .map((benefit) => ({
      benefit,
      score: similarityScore(target, benefit),
    }))
    .sort(compareSimilarBenefits)
}

function hasSharedCategory(target: BenefitSummary, candidate: BenefitSummary): boolean {
  return target.categories.some((category) => candidate.categories.includes(category))
}

export function findSimilarBenefits(
  target: BenefitSummary,
  allBenefits: BenefitSummary[],
  options: { limit?: number; minScore?: number } = {}
): SimilarBenefit[] {
  const limit = options.limit ?? DEFAULT_LIMIT
  const minScore = options.minScore ?? MIN_SCORE_THRESHOLD
  const ranked = rankCandidates(target, allBenefits)

  const aboveThreshold = ranked.filter((item) => item.score >= minScore)
  const results = aboveThreshold.slice(0, limit)

  if (results.length < 3) {
    const pickedIds = new Set(results.map((item) => item.benefit.id))
    const industryMatches = ranked.filter(
      (item) => !pickedIds.has(item.benefit.id) && item.benefit.industry === target.industry
    )
    for (const item of industryMatches) {
      if (results.length >= limit) break
      results.push(item)
      pickedIds.add(item.benefit.id)
    }
  }

  if (results.length < 3 && target.categories.length > 0) {
    const pickedIds = new Set(results.map((item) => item.benefit.id))
    const categoryMatches = ranked.filter(
      (item) => !pickedIds.has(item.benefit.id) && hasSharedCategory(target, item.benefit)
    )
    for (const item of categoryMatches) {
      if (results.length >= limit) break
      results.push(item)
    }
  }

  return results
}
